/**
 * FilterPanel Component
 * Displays primary filters (radio buttons - pick one) and additional filters (checkboxes - pick many)
 * for power inquiry search - styled to match site theme
 */

import { useState } from 'react';
import { ChevronDownIcon, FunnelIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { OathSpinner } from '../../../shared/OathUI';
import { OathCheckbox, OathRadio } from '../../../shared/OathTableComponents';

/**
 * Frontend descriptions for filters (supplements backend descriptions)
 * Used when backend doesn't provide descriptions or to override them
 */
const FILTER_DESCRIPTIONS = {
  RecentDischarges: '90 days and under',
  RecentForfeitures: '90 days and under',
  // Add more as needed
};

/**
 * Get description for a filter, preferring backend description over frontend mapping
 */
const getFilterDescription = (filter) => {
  return filter.description || FILTER_DESCRIPTIONS[filter.filterName] || null;
};

/**
 * Section Label Component
 */
const SectionLabel = ({ label, count }) => (
  <div className="flex items-center gap-2 py-2 px-1">
    <span className="text-[11px] sm:text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
      {label}
    </span>
    {count > 0 && (
      <span className="px-1.5 py-0.5 bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light text-[10px] sm:text-xs font-semibold rounded">
        {count}
      </span>
    )}
  </div>
);

/**
 * Filter Item Component
 */
const FilterItem = ({ filter, isSelected, onClick, type = 'radio' }) => {
  const description = getFilterDescription(filter);

  return (
    <div
      role={type}
      aria-checked={isSelected}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={`flex items-start gap-3 cursor-pointer py-2 px-2.5 rounded-lg transition-all duration-150 group ${
        isSelected
          ? 'bg-primary/5 dark:bg-primary/10 ring-1 ring-primary/20 dark:ring-primary/30'
          : 'hover:bg-gray-50 dark:hover:bg-gray-700/30'
      }`}
    >
      <div className="mt-[2px]">
        {type === 'radio' ? (
          <OathRadio checked={isSelected} />
        ) : (
          <OathCheckbox checked={isSelected} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <span
          className={`text-[13px] leading-tight block ${
            isSelected
              ? 'text-gray-900 dark:text-white font-medium'
              : 'text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white'
          }`}
        >
          {filter.label || filter.filterDescription}
        </span>
        {description && (
          <span className="text-[11px] sm:text-xs text-gray-400 dark:text-gray-500 mt-0.5 block">
            {description}
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Additional Filters section - shows checked filters up top, with a toggle to reveal the rest
 */
const AdditionalFiltersSection = ({ specificFilters, selectedSpecificFilters, onSpecificFilterToggle }) => {
  const [showAll, setShowAll] = useState(false);

  const sortAlpha = (a, b) => (a.label || a.filterDescription || '').localeCompare(b.label || b.filterDescription || '');
  const checkedFilters = specificFilters.filter(f => selectedSpecificFilters.includes(f.filterName)).sort(sortAlpha);
  const uncheckedFilters = specificFilters.filter(f => !selectedSpecificFilters.includes(f.filterName)).sort(sortAlpha);
  const hasChecked = checkedFilters.length > 0;

  return (
    <div className="space-y-1">
      <SectionLabel label="Additional Filters" count={selectedSpecificFilters.length} />
      <div className="space-y-1 pb-1">
        {/* Always show checked filters - compact with tooltip for description */}
        {checkedFilters.map((filter) => {
          const description = getFilterDescription(filter);
          return (
            <button
              key={filter.filterName}
              onClick={() => onSpecificFilterToggle(filter.filterName)}
              title={description || undefined}
              className="flex items-center gap-2 w-full px-2.5 py-1.5 sm:px-2 sm:py-1 text-[12px] sm:text-xs font-medium text-primary dark:text-primary-light bg-primary/5 dark:bg-primary/10 hover:bg-primary/10 dark:hover:bg-primary/20 rounded-md transition-colors text-left"
            >
              <OathCheckbox checked={true} />
              {filter.label || filter.filterDescription}
            </button>
          );
        })}

        {/* Toggle between checked and unchecked */}
        {uncheckedFilters.length > 0 && (
          <button
            onClick={() => setShowAll(!showAll)}
            className={`flex items-center gap-1.5 w-full px-2.5 py-1.5 text-xs font-medium text-gray-400 dark:text-gray-500 hover:text-primary dark:hover:text-primary-light hover:bg-gray-50 dark:hover:bg-gray-700/30 rounded-lg transition-colors group ${hasChecked ? 'mt-1 border-t border-gray-100 dark:border-gray-700/50 pt-1.5' : ''}`}
          >
            <ChevronDownIcon
              className={`h-3.5 w-3.5 transition-transform duration-200 ${showAll ? 'rotate-180' : ''}`}
            />
            {showAll ? 'Hide filters' : `+${uncheckedFilters.length} more filters`}
          </button>
        )}

        {/* Unchecked filters (shown when expanded) - compact single column */}
        {showAll && uncheckedFilters.length > 0 && (
          <div className="space-y-0.5 pt-1">
            {uncheckedFilters.map((filter) => (
              <button
                key={filter.filterName}
                onClick={() => onSpecificFilterToggle(filter.filterName)}
                className="flex items-center gap-2 w-full px-2.5 py-1.5 sm:px-2 sm:py-1 text-[12px] sm:text-xs text-gray-600 dark:text-gray-400 hover:bg-primary/5 hover:text-primary dark:hover:bg-primary/10 dark:hover:text-primary-light rounded-md transition-colors text-left"
              >
                <span className="w-3.5 h-3.5 rounded border border-gray-300 dark:border-gray-600 flex-shrink-0" />
                {filter.label || filter.filterDescription}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Filter panel with primary filters and additional filters
 */
const FilterPanel = ({
  groupFilters = [],
  specificFilters = [],
  selectedGroupFilter,
  selectedSpecificFilters = [],
  onGroupFilterChange,
  onSpecificFilterToggle,
  onClearFilters,
  isLoading = false,
  roundedTop = true,
}) => {
  const hasActiveFilters =
    (selectedGroupFilter && selectedGroupFilter !== 'AllPowers') ||
    selectedSpecificFilters.length > 0;

  const activeFilterCount =
    (selectedGroupFilter && selectedGroupFilter !== 'AllPowers' ? 1 : 0) +
    selectedSpecificFilters.length;

  if (isLoading) {
    return (
      <div
        className={`relative bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 p-6 overflow-hidden ${
          roundedTop ? 'rounded-xl' : 'rounded-b-xl'
        }`}
      >
        {/* Top accent bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
        <div className="flex flex-col items-center justify-center py-6">
          <OathSpinner size="md" />
          <span className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Loading filters...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`relative bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden ${
        roundedTop ? 'rounded-xl' : 'rounded-b-xl'
      }`}
    >
      {/* Top accent bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary/60 via-primary to-primary/60" />
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50/80 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-1.5 bg-primary/10 dark:bg-primary/20 rounded-lg">
              <FunnelIcon className="h-4 w-4 text-primary dark:text-primary-light" />
            </div>
            <div>
              <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
                Filters
              </h3>
              {hasActiveFilters && (
                <p className="text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">
                  {activeFilterCount} active
                </p>
              )}
            </div>
          </div>
          {hasActiveFilters && (
            <button
              onClick={onClearFilters}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
              title="Clear all filters"
            >
              <XMarkIcon className="h-3.5 w-3.5" />
              <span>Clear</span>
            </button>
          )}
        </div>
      </div>

      <div className="p-3 space-y-1">
        {/* Primary Filters - Radio Buttons (pick one, always visible) */}
        <div className="space-y-1 pb-2">
          {groupFilters.map((filter) => (
            <FilterItem
              key={filter.filterName}
              filter={filter}
              isSelected={selectedGroupFilter === filter.filterName}
              onClick={() => onGroupFilterChange(filter.filterName)}
              type="radio"
            />
          ))}
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 dark:border-gray-700/50 my-2" />

        {/* Additional Filters - Checkboxes (pick many) */}
        <AdditionalFiltersSection
          specificFilters={specificFilters}
          selectedSpecificFilters={selectedSpecificFilters}
          onSpecificFilterToggle={onSpecificFilterToggle}
        />
      </div>
    </div>
  );
};

export default FilterPanel;
